import { Box } from "@mui/material";
import { DateTime } from "luxon";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { initialRecurringShiftDataStructure, RecurringShiftComponentSource } from "../../../components/RecurringShift/RecurringShift";
import { RecurringShiftPanelWrapper } from "../../../components/RecurringShift/RecurringShiftPanelWrapper";
import { DaysInWeek } from "../../../components/RecurringShift/util/DaysInWeekSelector";
import { GlobalLocationOrder } from "../../../components/staffing/Header/GlobalLocationOrder";
import { SystemDropdown } from "../../../components/UIToolkit/Inputs/SystemDropDown";
import { RoleOrJobSelector } from "../../../components/UIToolkit/molecules/RoleOrJobSelector";
import { TimeFilterChips } from "../../../components/UIToolkit/molecules/TimeFilterChips";
import { SystemSortingButtonWithPopup } from "../../../components/UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import { consecutiveMasterScheduleSortingKey } from "../../../services/LocalStorageService";
import { STATUS_BADGE } from "../../../theme/tokens";
import { LONG_PTO_BUTTON_BOTTOM_RESERVE_PX } from "../LongPTOButton/consts";
import { ScheduleTemplateHeader } from "../ScheduleTemplateHeader";
import { CellMenuAction, RecurringMasterScheduleCellMenu } from "../v2/auxillary/RecurringMasterScheduleCellMenu";
import { contentCardStyle, masterScheduleDefaultSorting, masterScheduleSortingOptions, rootCardStyle } from "../v2/utils";
import { ConsecutivePeriodInput } from "./ConsecutivePeriodInput";
import { CONSECUTIVE_MASTER_SCHEDULE_ROOT_ID } from "./consts";
import { ConsecutiveGrid } from "./Grid/ConsecutiveGrid";
import { useConsecutiveMasterScheduleState } from "./useConsecutiveMasterScheduleState";

/** The Consecutive view reuses the Master Schedule cell menu but omits Delete (out of scope here). */
const HIDDEN_CELL_MENU_ACTIONS = [CellMenuAction.Delete];

export const ConsecutiveMasterSchedule = ({ trailing }: { trailing?: ReactNode }) => {
	const { t } = useTranslation();
	const {
		calculatedData,
		isCalculating,
		loadError,
		isStale,
		cycleStartDate,
		today,
		timezone,
		period,
		setPeriod,
		cycleLength,
		chosenDepartment,
		refetchRecurringShifts,
		expandedGroups,
		handleSectionCollapseToggle,
		filtersBoxRef,
		currentlyClickedCell,
		editOrCreateRecurringShiftState,
		onShiftClick,
		onShiftKeyDown,
		onShiftMenuAction,
		handleOnAddClick,
		handleRecurringShiftComponentOnClose,
		selectedShiftFilter,
		handleShiftFilterChange,
		masterScheduleSortState,
		setMasterScheduleSortState,
		selectedRoleFilters,
		onSelectedRoleFiltersChanged,
		departmentSpecificRoles,
		selectedJobFilters,
		onSelectedJobFiltersChanged,
		availableLocations,
		selectedLocationFilters,
		onSelectedLocationFiltersChanged,
		handleNurseFilterChange,
		selectedNurseToFilterBy,
		periodMatchingShifts,
	} = useConsecutiveMasterScheduleState();

	const uniqueSections = calculatedData ? Object.keys(calculatedData) : [];
	const locationDropdownItems = availableLocations.map((loc) => ({ value: loc, label: loc || t("schedule.template.no_location") }));

	return (
		<Box
			id={CONSECUTIVE_MASTER_SCHEDULE_ROOT_ID}
			data-testid="consecutive-master-schedule-root"
			data-stale={isStale}
			sx={{
				...rootCardStyle,
				"& .ms2-highlight": { backgroundColor: STATUS_BADGE.PEACH },
				"& [data-testid='consecutive-master-schedule-grid']": { transition: "opacity 200ms ease" },
				"&[data-stale='true'] [data-testid='consecutive-master-schedule-grid']": { opacity: 0.45, pointerEvents: "none" },
			}}
		>
			{currentlyClickedCell && (
				<RecurringMasterScheduleCellMenu
					onSelectedAction={onShiftMenuAction}
					open={!!currentlyClickedCell}
					anchorEl={currentlyClickedCell.anchorEl}
					hiddenActions={HIDDEN_CELL_MENU_ACTIONS}
				/>
			)}
			{editOrCreateRecurringShiftState && (
				<RecurringShiftPanelWrapper
					isSynergyMS={false}
					renderWithoutModal={false}
					clickedDate={editOrCreateRecurringShiftState.clickedDate}
					shouldAddNewShiftOnOpen={!editOrCreateRecurringShiftState.recurringShift?.nurseId}
					clickedNurseId={editOrCreateRecurringShiftState.recurringShift?.nurseId}
					clickedLocation={editOrCreateRecurringShiftState.clickedLocation}
					onClose={handleRecurringShiftComponentOnClose}
					source={RecurringShiftComponentSource.MasterSchedule}
					recurringShiftDisplayMode={editOrCreateRecurringShiftState.mode}
					existingRecurringShiftId={editOrCreateRecurringShiftState.recurringShift?.id}
					existingShiftStartTime={
						editOrCreateRecurringShiftState.recurringShift
							? editOrCreateRecurringShiftState.clickedDate
									.set({ hour: +editOrCreateRecurringShiftState.recurringShift.shiftStart.substring(0, 2) })
									.set({ minute: +editOrCreateRecurringShiftState.recurringShift.shiftStart.substring(3, 5) })
							: undefined
					}
					initialRecurringShiftData={{
						...initialRecurringShiftDataStructure,
						recurrenceValue: 2,
						selectedLocation: editOrCreateRecurringShiftState.clickedLocation,
						...(!editOrCreateRecurringShiftState.recurringShift
							? {
									selectedDays: [
										DaysInWeek[editOrCreateRecurringShiftState.clickedDate.setLocale("en").toFormat("cccc").toLowerCase() as keyof typeof DaysInWeek],
									],
								}
							: {}),
						shiftStart: DateTime.fromFormat(editOrCreateRecurringShiftState.clickedLocation.shift_start, "h:mm"),
						shiftEnd: DateTime.fromFormat(editOrCreateRecurringShiftState.clickedLocation.shift_end, "h:mm"),
					}}
				/>
			)}

			{/* Same title KEY as every other layout. This used to be `master.schedule`, which happens
			    to read "Schedule Template" in English but "Horario General" / "לוח זמנים ראשי"
			    (i.e. "Master Schedule") elsewhere — so a Spanish or Hebrew user saw the page rename
			    itself on this one tab. */}
			<ScheduleTemplateHeader
				dataTestId="consecutive-master-schedule-header"
				staffFilter={{ value: selectedNurseToFilterBy, onChange: handleNurseFilterChange }}
				trailing={trailing}
			/>

			<Box
				sx={[
					// Flex-sized, not header-measured — see the same box in MasterScheduleV2 for why subtracting
					// the header alone left the grid overflowing the card by its row gap.
					{
						p: 2,
						// Bottom inset is deliberately larger than the other three sides: it reserves the footprint of
						// the `position: fixed` Long-PTO button (see LongPTOButton/consts.ts), which would otherwise
						// cover — and swallow clicks on — the grid's last row. Spelled out as `paddingBottom` because
						// the `pb` shorthand would multiply the px value by 8.
						paddingBottom: `${LONG_PTO_BUTTON_BOTTOM_RESERVE_PX}px`,
						flex: 1,
						minHeight: 0,
					},
					...(Array.isArray(contentCardStyle) ? contentCardStyle : [contentCardStyle]),
				]}
			>
				<Box
					ref={filtersBoxRef}
					data-testid="consecutive-master-schedule-filters"
					sx={{ display: "flex", alignItems: "center", gap: 4, width: "100%", flexWrap: "wrap", mb: 1 }}
				>
					<TimeFilterChips
						allShiftsTimes={
							periodMatchingShifts?.map((value) => ({
								start: DateTime.fromFormat(value.shiftStart.substring(0, 5), "HH:mm"),
								end: DateTime.fromFormat(value.shiftEnd.substring(0, 5), "HH:mm"),
							})) ?? []
						}
						selectedFilters={[selectedShiftFilter]}
						onSelectedFilterChange={handleShiftFilterChange}
					/>
					<RoleOrJobSelector
						selectedRoles={selectedRoleFilters}
						onSelectedRolesChanged={onSelectedRoleFiltersChanged}
						availableRoles={departmentSpecificRoles}
						selectedJobIds={selectedJobFilters}
						onSelectedJobsChanged={onSelectedJobFiltersChanged}
						label="Job"
						formControlSx={{ width: "200px" }}
					/>
					{availableLocations.length > 0 && (
						<SystemDropdown
							multiple
							items={locationDropdownItems}
							selectedItems={selectedLocationFilters}
							onChangeMultiple={onSelectedLocationFiltersChanged}
							label={t("location")}
							showFilter
							formControlSx={{ width: "200px" }}
							data-testid="consecutive-master-schedule-location-filter"
						/>
					)}
					<SystemSortingButtonWithPopup
						sortingOptions={masterScheduleSortingOptions}
						initialState={masterScheduleSortState}
						setSortState={setMasterScheduleSortState}
						defaultSortState={masterScheduleDefaultSorting}
						localStorageTableKey={consecutiveMasterScheduleSortingKey}
					/>
					<GlobalLocationOrder />
					<Box
						sx={{
							display: "flex",
							flexGrow: 1,
							justifyContent: "end",
						}}
					>
						<ConsecutivePeriodInput period={period} onChange={setPeriod} />
					</Box>
				</Box>

				<Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
					<ConsecutiveGrid
						calculatedData={calculatedData}
						isCalculating={isCalculating}
						loadError={loadError}
						cycleStartDate={cycleStartDate}
						firstSunday={chosenDepartment.first_sunday}
						daysPerBlock={cycleLength}
						cycleLength={cycleLength}
						timezone={timezone}
						today={today}
						expandedGroups={expandedGroups}
						handleSectionCollapseToggle={handleSectionCollapseToggle}
						departmentSpecificRoles={departmentSpecificRoles}
						uniqueSections={uniqueSections}
						refetchRecurringShifts={refetchRecurringShifts}
						onShiftClick={onShiftClick}
						onShiftKeyDown={onShiftKeyDown}
						handleOnAddClick={handleOnAddClick}
					/>
				</Box>
			</Box>
		</Box>
	);
};
